import type { DividendMode, SimStep } from "./types";

/* ──────────────────────────────────────────────────────────────────────────
 * Year aggregator — bucket daily simulator output into year-end summaries.
 *
 * Used by the `/simulate` breakdown table. One row per year of the
 * simulation, plus a final totals row computed by the caller from the
 * summaries returned here.
 *
 * NET formula (per Slice 11 spec):
 *   net = (Portfolio - Loan) + cumulative_dividends_outside_portfolio
 *                            + cumulative_tax_refunds
 *
 * For DRIP mode, dividends are reinvested into portfolio value, so the
 * `outside_portfolio` term is 0 and the dividend column on the table
 * exists only as a visualization of the income stream (with a caption
 * elsewhere clarifying it's already reflected in portfolio growth).
 *
 * For ignore mode (e.g. the /learn explainer chart's scenario), the
 * dividend cash is genuinely outside the portfolio and the NET column
 * adds it.
 *
 * Pure function. Date strings are ISO YYYY-MM-DD throughout.
 * ────────────────────────────────────────────────────────────────────────── */

export interface YearSummary {
  /** Calendar year (e.g. 2015). */
  year: number;
  /** Loan balance at the year's last simulator step. */
  loanBalance: number;
  /** Portfolio value at the year's last simulator step. */
  portfolioValue: number;
  /** Sum of `interestAccruedThisMonth` across all steps in the year. */
  interestPaid: number;
  /** Sum of `cashDividendThisStep` across all steps in the year. */
  dividends: number;
  /** Tax refund for the year = `interestPaid × marginalRate`. Zero when income unknown. */
  taxRefund: number;
  /**
   * Cumulative net wealth created from a starting position of $0 personal money
   * (the leveraged-investing model — see /learn Act 4). Computed at year-end
   * using the year-end loan and portfolio plus all cumulative refunds (and
   * outside dividends for ignore mode) up to that year.
   */
  net: number;
}

export function aggregateByYear(
  steps: readonly SimStep[],
  marginalRate: number,
  dividendMode: DividendMode,
): YearSummary[] {
  if (steps.length === 0) return [];

  // Group steps by ISO year. Map preserves insertion order which, given the
  // simulator emits steps chronologically, gives us year buckets in order.
  const byYear = new Map<number, SimStep[]>();
  for (const step of steps) {
    const year = Number(step.date.slice(0, 4));
    let bucket = byYear.get(year);
    if (!bucket) {
      bucket = [];
      byYear.set(year, bucket);
    }
    bucket.push(step);
  }

  const summaries: YearSummary[] = [];
  let cumulativeDividends = 0;
  let cumulativeRefunds = 0;
  const includeDividendsInNet = dividendMode === "ignore";

  for (const [year, yearSteps] of byYear.entries()) {
    const lastStep = yearSteps[yearSteps.length - 1]!;
    const interestThisYear = yearSteps.reduce((sum, s) => sum + s.interestAccruedThisMonth, 0);
    const dividendsThisYear = yearSteps.reduce((sum, s) => sum + s.cashDividendThisStep, 0);
    const refundThisYear = interestThisYear * marginalRate;

    cumulativeDividends += dividendsThisYear;
    cumulativeRefunds += refundThisYear;

    const net =
      lastStep.portfolioValue -
      lastStep.loanBalance +
      cumulativeRefunds +
      (includeDividendsInNet ? cumulativeDividends : 0);

    summaries.push({
      year,
      loanBalance: lastStep.loanBalance,
      portfolioValue: lastStep.portfolioValue,
      interestPaid: interestThisYear,
      dividends: dividendsThisYear,
      taxRefund: refundThisYear,
      net,
    });
  }

  return summaries;
}
